import React from 'react';
import classes from './ExaminationsTab.module.css';
import { SalesOrderInfo } from '../../../../store/slices/salesOrders';
import { SkinExamination } from './SkinExamination';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import {
  fetchExaminations,
  selectExaminations,
  selectExaminationsStatus,
  startSingleExamination,
} from '../../../../store/slices/examinations';
import { ThroatExamination } from './ThroatExamination';
import { LeftEarExamination } from './LeftEarExamination';
import { HeartAuscultationExamination } from './HeartAuscultationExamination';
import { client } from '../../../../networking/client';
import { AbdomenExamination } from './AbdomenExamination';
import { CoughExamination } from './CoughExamination';
import { RightEarExamination } from './RighEarExamination';
import { toast } from 'react-toastify';
import { LungAuscultationExamination } from './LungAuscultationExamination';
import { AxiosResponse } from 'axios';
import { ExaminationButtons } from './ExaminationButtons';
import higoIcon from '../../../../assets/images/higo.png';
import { DetailType, ErrorDialog } from './ErrorDialog';
import higoExaminationsVideo from '../../../../assets/videos/higo-examinations.webm';
import higoVideo from '../../../../assets/videos/higo-plugins.webm';
import Spinner from '../../Spinner/Spinner';
import { IPerson, selectPersonLoggedIn } from '../../../../store/slices/auth';
import { TemperatureExamination } from './TemperatureExamination';
import { useTranslation } from '../../../../hooks/useTranslations';

type Props = {
  selectedSalesOrder: SalesOrderInfo;
  report?: boolean;
};

const ExaminationsTab: React.FC<Props> = ({ selectedSalesOrder, report }) => {
  const dispatch = useAppDispatch();
  const examinations = useAppSelector(selectExaminations);
  const examinationStatus = useAppSelector(selectExaminationsStatus);
  const [errorDialog, setErrorDialog] = React.useState<{ error: string; details: DetailType[] }>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [startedNewExamination, setStartedNewExamination] = React.useState<boolean>(false);
  const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const { translations } = useTranslation();

  const startExaminationById = async (examTypeId: number) => {
    let patientId = localStorage.getItem(
      `higo_patient_id${selectedSalesOrder.work_order?.work_order_id}`
    );

    try {
      const higoAccount = await client.get(
        `/healthpoint/higo_staff_account/${personLoggedIn?.person_id}`
      );
      const medicalOperatorId = +higoAccount?.data?.higoStaffAccount?.medical_operator_id;
      if (patientId === null) {
        const patientResponse: AxiosResponse<{ higo_patient_id: string }> = await client.post(
          '/healthpoint/patient/',
          {
            work_order_id: selectedSalesOrder.work_order?.work_order_id,
            medical_operator_id: typeof medicalOperatorId === 'number' ? medicalOperatorId : 0,
          }
        );
        patientId = patientResponse.data.higo_patient_id;
      }
      if (!patientId) {
        throw new Error();
      }
      dispatch(
        startSingleExamination({
          work_order_id: selectedSalesOrder.work_order?.work_order_id ?? 0,
          examination_id: examTypeId,
          higo_patient_id: patientId,
          medical_operator_id: typeof medicalOperatorId === 'number' ? medicalOperatorId : 0,
        })
      );
    } catch (error) {
      toast.error('Something went wrong', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const startExamination = async () => {
    setLoading(true);
    client.delete(
      `/healthpoint/cancel-examination/${selectedSalesOrder.work_order?.work_order_id}`
    );
    try {
      const higoAccount = await client.get(
        `/healthpoint/higo_staff_account/${personLoggedIn?.person_id}`
      );
      const medicalOperatorId = +higoAccount?.data?.higoStaffAccount?.medical_operator_id;
      const createPatientResponse = await client.post('/healthpoint/patient/', {
        work_order_id: selectedSalesOrder.work_order?.work_order_id,
        medical_operator_id: typeof medicalOperatorId === 'number' ? medicalOperatorId : 0,
      });
      await client.post('/healthpoint/examination/ ', {
        work_order_id: selectedSalesOrder.work_order?.work_order_id,
        higo_patient_id: createPatientResponse.data.higo_patient_id,
        medical_operator_id: typeof medicalOperatorId === 'number' ? medicalOperatorId : 0,
      });
      if (selectedSalesOrder.work_order?.work_order_id) {
        setStartedNewExamination(true);
        dispatch(fetchExaminations(selectedSalesOrder.work_order?.work_order_id));
      }
    } catch (error: any) {
      if (error?.response?.data?.error && error?.response?.data?.details) {
        setErrorDialog(error.response.data);
      }
      toast.error(error.response?.data?.error ?? error.response?.data?.message ?? error.message, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (selectedSalesOrder.work_order?.work_order_id) {
      dispatch(fetchExaminations(selectedSalesOrder.work_order?.work_order_id));
    }
  }, [dispatch, selectedSalesOrder.work_order?.work_order_id, startedNewExamination]);

  React.useEffect(() => {
    if (initialLoading && examinationStatus === 'fulfilled') {
      setInitialLoading(false);
    }
  }, [examinationStatus]);

  const hasStartedExamination = React.useMemo(() => {
    return !!examinations.find((examination) => examination.work_order_examination_id);
  }, [examinations]);

  const isExaminationComplete = React.useMemo(() => {
    return !!examinations.find((examination) => examination.attachments);
  }, [examinations]);

  if (initialLoading) {
    return (
      <div className={classes.container}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {report &&
        !hasStartedExamination &&
        !startedNewExamination &&
        selectedSalesOrder?.work_order?.work_order_status_id === 2 && (
          <div className="emptyState">{translations('label.empty_state')}</div>
        )}
      <div className={classes.container}>
        {!report && (
          <ExaminationButtons
            selectedSalesOrder={selectedSalesOrder}
            hasStartedExamination={hasStartedExamination}
            isExaminationComplete={isExaminationComplete}
            handleStartExamination={startExamination}
            loading={loading}
            setLoading={setLoading}
            startedNewExamination={startedNewExamination}
            setStartedNewExamination={setStartedNewExamination}
          />
        )}
        {(!hasStartedExamination || startedNewExamination) && !report && (
          <div
            style={{
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              height: '100%',
            }}
          >
            <video
              src={
                startedNewExamination && hasStartedExamination ? higoVideo : higoExaminationsVideo
              }
              autoPlay
              loop
            />
            <img
              style={{ position: 'absolute', bottom: 40, left: 50, height: 60 }}
              src={higoIcon}
              alt={'higo'}
            />
          </div>
        )}
        {!startedNewExamination && hasStartedExamination && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <LeftEarExamination report={report} startExamination={startExaminationById} />
              </div>
              <div style={{ flex: 1 }}>
                <RightEarExamination report={report} startExamination={startExaminationById} />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <ThroatExamination report={report} startExamination={startExaminationById} />
              </div>
              <div style={{ flex: 1 }}>
                <SkinExamination report={report} startExamination={startExaminationById} />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <HeartAuscultationExamination
                  report={report}
                  startExamination={startExaminationById}
                />
              </div>
              <div style={{ flex: 1 }}>
                <LungAuscultationExamination
                  report={report}
                  startExamination={startExaminationById}
                />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <AbdomenExamination report={report} startExamination={startExaminationById} />
              </div>
              <div style={{ flex: 1 }}>
                <CoughExamination report={report} startExamination={startExaminationById} />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <TemperatureExamination
                  report={report}
                  startExamination={startExaminationById}
                  workOrderId={selectedSalesOrder.work_order?.work_order_id}
                />
              </div>
              <div style={{ flex: 1 }}></div>
            </div>
          </>
        )}
        {errorDialog && (
          <ErrorDialog
            error={errorDialog.error}
            details={errorDialog.details}
            isOpen={!!errorDialog}
            onClose={() => {
              setErrorDialog(undefined);
            }}
          />
        )}
      </div>
    </>
  );
};

export { ExaminationsTab };
